import { useEffect, useMemo, useState } from "react";

import { ShowBadge, getShowBadges } from "@/data/showBadges";
import { ShowInfo } from "@/data/shows";

export function useShowRowBadges(
  shows: ShowInfo[],
  { enabled = true }: { enabled?: boolean } = {}
) {
  const [badges, setBadges] = useState<Record<ShowInfo["id"], ShowBadge[]>>({});
  const [badgesLoading, setBadgesLoading] = useState(false);

  // get a stable value for the show ids
  const showIds = useMemo(() => shows.map((s) => s.id).join(","), [shows]);

  // Fetch badges on component mount rather than waiting for visibility, unless disabled
  useEffect(() => {
    if (!enabled) return;

    async function fetchBadges() {
      setBadgesLoading(true);
      try {
        // Filter out Ticketmaster show IDs (starting with "tm-") as they don't have badges in our system
        const platformShowIds = showIds
          .split(",")
          .filter((id) => !id.startsWith("tm-"));

        // Only fetch badges if we have platform show IDs
        if (platformShowIds.length > 0) {
          const badgeData = await getShowBadges(platformShowIds);

          const badgeMap: Record<ShowInfo["id"], ShowBadge[]> = {};

          badgeData.forEach((badge) => {
            badgeMap[badge.show_id] = [
              ...(badgeMap[badge.show_id] || []),
              badge,
            ];
          });

          setBadges((prev) => ({ ...prev, ...badgeMap }));
        }
      } catch (error) {
        console.error("Error fetching badges:", error);
      } finally {
        setBadgesLoading(false);
      }
    }

    fetchBadges();
  }, [showIds, enabled]);

  // Find specific badges - memoize these calculations
  const { usersChecking, isHeatingUp } = useMemo(() => {
    let totalUsersChecking = 0;
    let overallIsHeatingUp = false;

    shows.forEach((show) => {
      const showBadges = badges[show.id] || [];

      // check whether it's "heating up"
      const heatingUpBadge = showBadges.find(
        (badge) => badge.badge_type === "heating_up"
      );

      // check how many users are checking
      const usersCheckingBadge = showBadges.find(
        (badge) => badge.badge_type === "users_checking"
      );

      // Get the number of users checking
      const usersChecking = usersCheckingBadge?.num_users || 0;

      // Determine if the show is heating up
      const isHeatingUp = !!heatingUpBadge;

      // increment the total users checking
      totalUsersChecking += usersChecking;

      // consider the overall show as "heating up" if any of the shows are "heating up"
      overallIsHeatingUp = overallIsHeatingUp || isHeatingUp;
    });

    return {
      usersChecking: totalUsersChecking,
      isHeatingUp: overallIsHeatingUp,
    };
  }, [badges, shows]);

  return { usersChecking, isHeatingUp, badgesLoading };
}
